import React from "react";

import { ButtonCore } from "@braintrust/braintrust-ui-components";
import { Snackbar } from "@js/components/snackbar";
import type { WebSocketClient } from "@js/components/websocket/websocket-client";
import type { AppDispatch } from "@js/store";

import { PING } from "../action-types";
import {
  BACKEND_REVISION,
  PING_MESSAGE,
  PING_PONG_FAILURE_TIME_LIMIT,
} from "../constants";

export class PingPong {
  private firstPingSentAt: number | undefined;
  private pingWaitningForPongSentAt: number | undefined;

  private customWebSocket: WebSocketClient;

  private readonly dispatch: AppDispatch;

  constructor(webSocket: WebSocketClient, dispatch: AppDispatch) {
    this.customWebSocket = webSocket;
    this.dispatch = dispatch;
  }

  public resetTimestamps = (): void => {
    this.firstPingSentAt = undefined;
    this.pingWaitningForPongSentAt = undefined;
  };

  public isUp = (): boolean => {
    if (!this.pingWaitningForPongSentAt && this.firstPingSentAt) {
      return true;
    }

    return (
      Date.now() - (this.pingWaitningForPongSentAt || 0) <
      PING_PONG_FAILURE_TIME_LIMIT
    );
  };

  public isInitializing = (): boolean => {
    if (!this.firstPingSentAt) {
      return true;
    }

    return !this.isInitializingTimeLimitExceeded();
  };

  public isInitializingTimeLimitExceeded = (): boolean => {
    return (
      Date.now() - (this.firstPingSentAt || 0) > PING_PONG_FAILURE_TIME_LIMIT
    );
  };

  public ping = (): void => {
    if (!this.customWebSocket.isOpen()) {
      return;
    }

    if (this.pingWaitningForPongSentAt) {
      return; // waiting for pong
    }

    this.customWebSocket.send(PING_MESSAGE);
    this.dispatch({
      type: PING,
    });

    const now = Date.now();
    this.pingWaitningForPongSentAt = now;

    if (!this.firstPingSentAt) {
      this.firstPingSentAt = now;
    }
  };

  public onReceive = (payload: unknown): void => {
    this.pingWaitningForPongSentAt = undefined;

    if (this._isNewerBackendRevision(payload)) {
      this._displaySnackbarWithWarning();
    }
  };

  private _isNewerBackendRevision = (payload: unknown): boolean => {
    return (
      !!payload &&
      typeof payload === "object" &&
      BACKEND_REVISION in payload &&
      payload.BACKEND_REVISION !== SETTINGS.BACKEND_REVISION
    );
  };

  private _displaySnackbarWithWarning = (): void => {
    Snackbar.warning(
      `A new version of the app is available. It's highly recommended to
            refresh the site to make sure you won't experience any issues.`,
      {
        persist: true,
        preventDuplicate: true,
        action: (
          <ButtonCore
            disableRipple
            className="snackbar__link snackbar__link--warning"
            onClick={() => window.location.reload()}
          >
            Refresh
          </ButtonCore>
        ),
      },
    );
  };
}
